import { useEffect, useState } from 'preact/hooks';
import type { PhoneApp } from '../app/phone.js';
import { CLOCK_SKEW_WARN_SEC } from '../app/clock.js';
import { micConstraints } from '../media/devices.js';

type Status = 'idle' | 'running' | 'ok' | 'amber' | 'red';

function Item({ label, status, detail, action, onAction }: { label: string; status: Status; detail?: string; action?: string; onAction?: () => void }) {
  const cls = status === 'ok' ? 'status-ok' : status === 'amber' ? 'status-amber' : status === 'red' ? 'status-red' : 'sub';
  const text = status === 'running' ? 'checking…' : status === 'idle' ? '' : status.toUpperCase();
  return (
    <li>
      <span style={{ flex: 1 }}>{label}</span>
      <span class={cls}>{text}</span>
      {detail ? <span class="sub">{detail}</span> : null}
      {action && onAction ? <button onClick={onAction}>{action}</button> : null}
    </li>
  );
}

/** 2 s RMS sample of the selected microphone */
async function micLevel(constraints: MediaStreamConstraints): Promise<number> {
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  const ctx = new AudioContext();
  try {
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let peak = 0;
    const until = Date.now() + 2000;
    while (Date.now() < until) {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      peak = Math.max(peak, Math.sqrt(sum / buf.length));
      await new Promise((r) => setTimeout(r, 100));
    }
    return peak;
  } finally {
    for (const t of stream.getTracks()) t.stop();
    await ctx.close().catch(() => undefined);
  }
}

export function SelfCheck({ app }: { app: PhoneApp }) {
  const [mic, setMic] = useState<{ s: Status; d?: string }>({ s: 'idle' });
  const [spk, setSpk] = useState<Status>('idle');
  const skew = app.clock.skewSec();
  const turn = app.turnStatus.value;
  useEffect(() => {
    void app.refreshDevices();
  }, [app]);
  return (
    <section class="card self-check" aria-label="Self-check">
      <div class="who">Self-check</div>
      <ul class="list">
        <Item
          label="Microphone"
          status={mic.s}
          detail={mic.d}
          action="Test (speak for 2 s)"
          onAction={() => {
            setMic({ s: 'running' });
            micLevel(micConstraints(app.devicePrefs.value))
              .then((rms) => {
                const db = rms > 0 ? 20 * Math.log10(rms) : -100;
                setMic({ s: db > -40 ? 'ok' : 'amber', d: `${db.toFixed(0)} dBFS` });
              })
              .catch((e: Error) => setMic({ s: 'red', d: e.message }));
          }}
        />
        <Item
          label="Speaker"
          status={spk}
          action="Play tone"
          onAction={() => {
            setSpk('running');
            app
              .enableSound()
              .then(() => app.audio.testSpeaker())
              .then((ok) => setSpk(ok ? 'ok' : 'red'))
              .catch(() => setSpk('red'));
          }}
        />
        <Item label="TURN relay" status={turn === 'ok' ? 'ok' : turn === 'unreachable' ? 'amber' : turn === 'unsupported' ? 'red' : 'running'} detail={turn === 'unreachable' ? 'retrying every 60 s' : undefined} />
        <Item label="Clock" status={skew > CLOCK_SKEW_WARN_SEC ? 'amber' : 'ok'} detail={`${skew.toFixed(0)} s from the server`} />
        <Item label="Sound activation" status={app.audio.unlocked ? 'ok' : 'amber'} action={app.audio.unlocked ? undefined : 'Enable'} onAction={() => void app.enableSound()} />
        <Item label="Keep-alive channel" status={app.keepaliveHealthy.value ? 'ok' : 'amber'} />
        <Item label="Installed as app" status={app.install.installed.value ? 'ok' : 'amber'} detail={app.install.installed.value ? undefined : 'Install phone, then run on login via chrome://apps or the policy pack'} />
        <li>
          <span style={{ flex: 1 }}>Headsets (WebHID)</span>
          <span class="sub">{app.hidDevices.value.length > 0 ? app.hidDevices.value.map((h) => h.productName).join(', ') : 'none paired'}</span>
          <button onClick={() => void app.pairHeadset()}>Pair</button>
        </li>
      </ul>
      <div class="sub">Hook, mute and ring are verified per device after pairing; Jabra and Poly need their vendor host software for full control.</div>
    </section>
  );
}
